// scheduling/components/NonBillBudgetChart.jsx
// Stage 28 polish — donut chart visualizing non-billable budget allocation.
// Used on:
//   - NonBillBudgetTab: interactive preview as user types allocations.
//     Unallocated portion shows as gray; slices fill in live.
//   - NonBillProfilePanel: read-only view, shown AFTER submit only.
//
// Pure SVG, no external charting dep.

// Color palette for the 10 seeded categories. Stable + visually distinct.
// Categories not in this map fall back to a deterministic hash-based color.
const CATEGORY_COLORS = {
  'billing':       '#3B82F6', // blue
  'cpe-training':  '#8B5CF6', // purple
  'homeroom':      '#EC4899', // pink
  'management':    '#F59E0B', // amber
  'marketing':     '#10B981', // emerald
  'meetings':      '#06B6D4', // cyan
  'other':         '#9CA3AF', // gray
  'pto':           '#EF4444', // red
  'recruiting-hr': '#14B8A6', // teal
  'software':      '#84CC16', // lime
};
const FALLBACK_PALETTE = ['#7C3AED', '#F97316', '#0EA5E9', '#22C55E', '#D946EF', '#65A30D'];
const UNALLOCATED_COLOR = '#E5E7EB'; // light gray for the "remaining" slice

function colorFor(key) {
  if (CATEGORY_COLORS[key]) return CATEGORY_COLORS[key];
  // Deterministic hash → palette index. Stable across renders / sessions.
  let h = 0;
  for (let i = 0; i < key.length; i++) h = ((h << 5) - h + key.charCodeAt(i)) | 0;
  return FALLBACK_PALETTE[Math.abs(h) % FALLBACK_PALETTE.length];
}

// Convert (centerX, centerY, radius, angleDeg) → (x, y). Angle measured
// clockwise from 12 o'clock.
function polarToCartesian(cx, cy, r, angleDeg) {
  const angleRad = ((angleDeg - 90) * Math.PI) / 180;
  return {
    x: cx + r * Math.cos(angleRad),
    y: cy + r * Math.sin(angleRad),
  };
}

// Build an SVG path describing a donut-slice (annular sector) from
// startAngle → endAngle, between innerRadius and outerRadius.
function describeArc(cx, cy, outerR, innerR, startAngle, endAngle) {
  const startOuter = polarToCartesian(cx, cy, outerR, endAngle);
  const endOuter   = polarToCartesian(cx, cy, outerR, startAngle);
  const startInner = polarToCartesian(cx, cy, innerR, endAngle);
  const endInner   = polarToCartesian(cx, cy, innerR, startAngle);
  const largeArc = endAngle - startAngle <= 180 ? 0 : 1;
  return [
    'M', startOuter.x, startOuter.y,
    'A', outerR, outerR, 0, largeArc, 0, endOuter.x, endOuter.y,
    'L', endInner.x, endInner.y,
    'A', innerR, innerR, 0, largeArc, 1, startInner.x, startInner.y,
    'Z',
  ].join(' ');
}

export default function NonBillBudgetChart({
  categories,        // [{ CategoryKey, DisplayName, SortOrder }]
  allocations,       // { CategoryKey → hours } — current values (live or saved)
  totalEffective,    // annual TOTAL budget
  size = 220,
  showLegend = true,
  centerLabel,       // optional override; otherwise renders "Allocated / Total"
}) {
  const radius = size / 2;
  const outerR = radius - 4;
  const innerR = outerR * 0.62;
  const cx = radius;
  const cy = radius;

  // Build slices: one per category with allocation > 0, sorted by SortOrder
  // so the chart's slice order matches the table.
  const slices = (categories || [])
    .filter(c => (allocations[c.CategoryKey] || 0) > 0)
    .sort((a, b) => (a.SortOrder || 0) - (b.SortOrder || 0))
    .map(c => ({
      key: c.CategoryKey,
      label: c.DisplayName,
      value: Number(allocations[c.CategoryKey] || 0),
      color: colorFor(c.CategoryKey),
    }));

  const allocSum = slices.reduce((s, x) => s + x.value, 0);
  const total = totalEffective != null && totalEffective > 0 ? Number(totalEffective) : null;
  const remaining = total != null ? Math.max(0, total - allocSum) : 0;

  // Render slices + unallocated remainder. If total is null, no remainder
  // slice — chart shows just allocations as a 100% pie.
  const renderTotal = total != null ? total : allocSum;
  let cumulative = 0;
  const paths = [];
  for (const s of slices) {
    const startAngle = (cumulative / renderTotal) * 360;
    cumulative += s.value;
    const endAngle = (cumulative / renderTotal) * 360;
    paths.push({ ...s, startAngle, endAngle });
  }
  if (total != null && remaining > 0.01) {
    const startAngle = (cumulative / renderTotal) * 360;
    paths.push({
      key: '__unallocated',
      label: 'Unallocated',
      value: remaining,
      color: UNALLOCATED_COLOR,
      startAngle,
      endAngle: 360,
    });
  }
  // If no allocations AND no total, draw a single empty gray ring.
  const empty = paths.length === 0;

  // Center label.
  const pct = total != null ? Math.round((allocSum / total) * 100) : 0;

  return (
    <div className="nonbill-chart-wrap">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="nonbill-chart-svg">
        {empty ? (
          // Empty gray donut — no allocation yet AND no total.
          <path
            d={describeArc(cx, cy, outerR, innerR, 0, 359.99)}
            fill={UNALLOCATED_COLOR}
          />
        ) : paths.length === 1 && paths[0].endAngle - paths[0].startAngle >= 359.99 ? (
          // Single full-circle slice — describeArc breaks at 360. Special case
          // it as a pair of half-circles.
          <>
            <path d={describeArc(cx, cy, outerR, innerR, 0, 180)} fill={paths[0].color}>
              <title>{paths[0].label}: {Math.round(paths[0].value * 100) / 100}h</title>
            </path>
            <path d={describeArc(cx, cy, outerR, innerR, 180, 359.99)} fill={paths[0].color}>
              <title>{paths[0].label}: {Math.round(paths[0].value * 100) / 100}h</title>
            </path>
          </>
        ) : (
          paths.map(p => (
            <path
              key={p.key}
              d={describeArc(cx, cy, outerR, innerR, p.startAngle, Math.min(p.endAngle, 359.99))}
              fill={p.color}
            >
              <title>{p.label}: {Math.round(p.value * 100) / 100}h{total != null ? ` (${Math.round((p.value / total) * 100)}%)` : ''}</title>
            </path>
          ))
        )}
        {/* Center text */}
        {centerLabel ? (
          <text x={cx} y={cy} className="nonbill-chart-center-text" textAnchor="middle" dominantBaseline="middle">
            {centerLabel}
          </text>
        ) : total != null ? (
          <>
            <text x={cx} y={cy - 10} className="nonbill-chart-center-num" textAnchor="middle" dominantBaseline="middle">
              {pct}%
            </text>
            <text x={cx} y={cy + 14} className="nonbill-chart-center-sub" textAnchor="middle" dominantBaseline="middle">
              allocated
            </text>
          </>
        ) : null}
      </svg>

      {showLegend && (
        <div className="nonbill-chart-legend">
          {slices.length === 0 && (
            <div className="nonbill-chart-legend-empty">No allocation yet</div>
          )}
          {slices.map(s => (
            <div key={s.key} className="nonbill-chart-legend-item">
              <span className="nonbill-chart-legend-swatch" style={{ background: s.color }} />
              <span className="nonbill-chart-legend-label">{s.label}</span>
              <span className="nonbill-chart-legend-value">
                {Math.round(s.value * 100) / 100}h
                {total != null && <span className="nonbill-chart-legend-pct"> ({Math.round((s.value / total) * 100)}%)</span>}
              </span>
            </div>
          ))}
          {total != null && remaining > 0.01 && (
            <div className="nonbill-chart-legend-item nonbill-chart-legend-item--remaining">
              <span className="nonbill-chart-legend-swatch" style={{ background: UNALLOCATED_COLOR }} />
              <span className="nonbill-chart-legend-label">Unallocated</span>
              <span className="nonbill-chart-legend-value">
                {Math.round(remaining * 100) / 100}h
                <span className="nonbill-chart-legend-pct"> ({Math.round((remaining / total) * 100)}%)</span>
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
